import { Accordion, AccordionItem } from '@design-system';
import { formatDate } from '@utils/datetime';
import { isReleaseTypeBlockchainAuction } from '@utils/product';
import {
  isStoryblokRichTextPresent,
  renderStoryblokRichText,
  stringToStoryblokRichText,
} from '@utils/renderStoryblokRichText';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { render } from 'storyblok-rich-text-react-renderer-ts';

import { pathFor } from '../../../helpers/links';
import { useCertificateOfAuthenticity } from '../../../hooks/useCertificateOfAuthenticity';
import { useCountryCode } from '../../../hooks/useCountryCode';
import { useTaxTypeTransactionDetails } from '../../../hooks/useTaxTypeTransactionDetails';
import { DrawStage } from '../../../types/Draw';
import { GeneratedTaxType } from '../../../types/generated';
import {
  AssetStoryblok,
  NftAuctionStoryblok,
  PackagingDetailsStoryblok,
  ProductDimensionsStoryblok,
  RichtextStoryblok,
} from '../../../types/generated-storyblok';
import { ProductBenefits } from '../../../types/Products';
import { Image } from '../../Image';
import { RichTextLink } from '../../RichTextLink';
import Dimensions from '../Dimensions';

type ProductAccordionProps = {
  auction?: NftAuctionStoryblok;
  open?: boolean;
  productId: string;
  editionSize?: number;
  releaseType?: StoryblokReleaseType;
  description?: RichtextStoryblok;
  dimensions?: ProductDimensionsStoryblok;
  customDimensions?: RichtextStoryblok;
  framingOptions?: string;
  isFrameIncluded?: boolean;
  isPreOrder?: boolean;
  packagingDetails?: PackagingDetailsStoryblok;
  medium?: string;
  authentication?: string;
  tags?: string[];
  finishing?: string;
  eligibility?: RichtextStoryblok;
  launchDate?: string;
  isShippingRequired?: boolean;
  noTaxCharged?: boolean;
  benefits?: ProductBenefits | null;
  drawStage?: DrawStage;
  onFramingDetailsClick?: () => void;
  onTaxDetailsClick?: () => void;
  noCoa: boolean;
};

export const ProductAccordion = ({
  auction,
  authentication,
  benefits,
  customDimensions,
  description,
  dimensions,
  drawStage,
  editionSize,
  eligibility,
  finishing,
  framingOptions,
  isFrameIncluded,
  isPreOrder,
  isShippingRequired,
  launchDate,
  medium,
  noCoa,
  noTaxCharged,
  onFramingDetailsClick,
  onTaxDetailsClick,
  open = true,
  packagingDetails,
  productId,
  releaseType,
  tags,
}: ProductAccordionProps) => {
  const { t } = useTranslation('product');
  const [countryCode] = useCountryCode();
  const taxes = useTaxTypeTransactionDetails(countryCode);

  const isDrawerNotNeeded =
    (taxes?.taxType === GeneratedTaxType.Vat ||
      taxes?.taxType === GeneratedTaxType.NoTax) &&
    benefits?.hasFreeShipping;

  const isNftAuction =
    releaseType && isReleaseTypeBlockchainAuction(releaseType);
  const certificate = useCertificateOfAuthenticity(noCoa, tags || []);

  const isDraw =
    releaseType === 'draw-release' && drawStage === 'DRAW_PRE_ORDER_CLOSED';

  const hasPackagingDetails = packagingDetails !== undefined;
  const showShippingTaxes =
    isShippingRequired && !noTaxCharged && taxes && !isNftAuction;

  const hasPaypalEnabled = benefits?.hasPaypalEnabled ?? false;
  const hasCryptoEnabled = benefits?.hasCryptoEnabled ?? false;
  const hasCryptoDrawEnabled = benefits?.hasCryptoDrawEnabled ?? false;
  const hasKlarnaEnabled = benefits?.hasKlarnaEnabled ?? false;
  const hasResaleAgreement = benefits?.hasResaleAgreement ?? false;

  const getPackagingDescription = () => {
    if (
      packagingDetails?.description &&
      isStoryblokRichTextPresent(packagingDetails.description)
    ) {
      return renderStoryblokRichText(packagingDetails.description, {
        paragraphClassName: 'pb-0',
      });
    }

    return renderStoryblokRichText(
      stringToStoryblokRichText(getPackagingPreset()?.description ?? ''),
      {
        paragraphClassName: 'pb-0',
      },
    );
  };

  const getPackagingPreset = () => {
    switch (packagingDetails?.preset) {
      case 'print-wooden':
        return {
          description: t('packaging.presets.print-wooden.description'),
          image: t('packaging.presets.print-wooden.image'),
        } as const;
      case 'print-non-wooden':
        return {
          description: t('packaging.presets.print-non-wooden.description'),
          image: t('packaging.presets.print-non-wooden.image'),
        } as const;
      case 'sculpture':
        return {
          description: t('packaging.presets.sculpture.description'),
          image: t('packaging.presets.sculpture.image'),
        } as const;
      default:
        return {} as const;
    }
  };

  const getPackagingImage = (): AssetStoryblok => {
    if (packagingDetails?.image?.filename) {
      return packagingDetails.image;
    }

    return {
      id: 1,
      name: 'Avant Arte Packaging',
      filename: getPackagingPreset()?.image ?? '',
    };
  };

  const getPaymentLabel = () => {
    if (hasCryptoDrawEnabled) {
      return t('benefits.payment.messageDrawCrypto');
    }

    if (isDraw || isPreOrder) {
      return t('benefits.payment.messageDraw');
    }

    return t('benefits.payment.message', {
      crypto: hasCryptoEnabled ? t('benefits.payment.messageCrypto') : '',
      payPal: hasPaypalEnabled ? t('benefits.payment.messagePayPal') : '',
      klarna: hasKlarnaEnabled ? t('benefits.payment.messageKlarna') : '',
    });
  };

  return (
    <Accordion>
      <AccordionItem
        key={`${productId}-1`}
        index={1}
        isOpen={open}
        subject={t('accordion.aboutThisWork')}
      >
        <div className="aa-prose">
          {description && isStoryblokRichTextPresent(description)
            ? render(description, {
                markResolvers: { link: RichTextLink },
              })
            : '–'}
        </div>
      </AccordionItem>
      <AccordionItem
        key={`${productId}-2`}
        index={2}
        subject={t('accordion.editionDetails')}
      >
        {dimensions ? (
          <div>
            <p className="font-bold">{t('dimensionsTitle')}</p>
            <Dimensions dimensions={dimensions} />
          </div>
        ) : null}
        {customDimensions && isStoryblokRichTextPresent(customDimensions) ? (
          <div>
            <p className="font-bold">{t('customDimensionsTitle')}</p>
            {renderStoryblokRichText(customDimensions, {
              paragraphClassName: 'pb-0',
            })}
          </div>
        ) : null}
        {framingOptions ? (
          <div>
            <p className="font-bold">{t('framingOptionsTitle')}</p>
            {framingOptions}
            {!isFrameIncluded && onFramingDetailsClick && (
              <>
                &nbsp;
                <button
                  className="hyperlink"
                  onClick={onFramingDetailsClick}
                  type="button"
                >
                  {t('product:seeDetails')}
                </button>
              </>
            )}
          </div>
        ) : null}
        {medium ? (
          <div>
            <p className="font-bold">{t('mediumTitle')}</p>
            {medium}
          </div>
        ) : null}
        {editionSize ? (
          <div>
            <p className="font-bold">{t('editionSizeLabel')}</p>
            {editionSize}
          </div>
        ) : null}
        {auction?.blockchain ? (
          <div>
            <p className="font-bold">{t('blockchain')}</p>
            {auction.blockchain}
          </div>
        ) : null}
        {auction?.tokenStandard ? (
          <div>
            <p className="font-bold">{t('tokenStandard')}</p>
            {auction.tokenStandard}
          </div>
        ) : null}
        {authentication ? (
          <div>
            <p className="font-bold">{t('authenticationTitle')}</p>
            {authentication}
          </div>
        ) : null}
        {certificate ? (
          <div>
            <p className="font-bold">{t('coaTitle')}</p>
            {certificate}
          </div>
        ) : null}
        {finishing ? (
          <div>
            <p className="font-bold">{t('finishingTitle')}</p>
            {finishing}
          </div>
        ) : null}
        {eligibility && isStoryblokRichTextPresent(eligibility) ? (
          <div>
            <p className="font-bold">{t('eligibilityTitle')}</p>
            {renderStoryblokRichText(eligibility, {
              paragraphClassName: 'pb-0',
            })}
          </div>
        ) : null}
        {launchDate ? (
          <div>
            <p className="font-bold">{t('launchDateTitle')}</p>
            {formatDate(launchDate)}
          </div>
        ) : null}
      </AccordionItem>
      {hasPackagingDetails ? (
        <AccordionItem
          key={`${productId}-3`}
          index={3}
          subject={t('accordion.packaging')}
        >
          {getPackagingDescription()}

          {getPackagingImage().filename !== '' ? (
            <Image
              alt={getPackagingImage().alt || getPackagingImage().name}
              hoverEffect
              shape="rounded"
              size={{
                sm: { ratio: '4/3', span: 12 },
                md: { ratio: '4/3', span: 6 },
                lg: { ratio: '4/3', span: 4 },
              }}
              src={getPackagingImage().filename}
            />
          ) : null}
        </AccordionItem>
      ) : null}
      <AccordionItem
        key={`${productId}-4`}
        index={hasPackagingDetails ? 4 : 3}
        subject={
          isNftAuction ? t('accordion.payment') : t('accordion.paymentShipping')
        }
      >
        <div>
          <p className="font-bold">{t('benefits.payment.title')}</p>
          <p>{getPaymentLabel()}</p>
        </div>
        {!isNftAuction ? (
          <div>
            <p className="font-bold">{t('benefits.policies.title')}</p>
            <p>
              {isDraw && (
                <>
                  <Link
                    className="hyperlink"
                    href={pathFor({
                      page: 'SUPPORT',
                      slug: 'terms-and-conditions',
                      anchor: 'draws',
                    })}
                    prefetch={false}
                    target="_blank"
                  >
                    {t('drawPolicy')}
                  </Link>
                  <br />
                </>
              )}
              <Link
                className="hyperlink"
                href={pathFor({ page: 'SUPPORT', slug: 'returns' })}
                prefetch={false}
                target="_blank"
              >
                {t('product:returnsRefunds')}
              </Link>
            </p>
            {hasResaleAgreement && (
              <p className="pt-3">
                {t('benefits.resaleAgreement.required')}
                &nbsp;
                <Link
                  className="hyperlink"
                  href={pathFor({
                    page: 'SUPPORT',
                    slug: 'shipping',
                    anchor: 'what-is-a-resale-agreement',
                  })}
                  prefetch={false}
                  target="_blank"
                >
                  {t('benefits.resaleAgreement.seeDetails')}
                </Link>
              </p>
            )}
          </div>
        ) : null}
        {showShippingTaxes && (
          <div>
            <p className="font-bold">{t('benefits.shipping.title')}</p>
            <p>
              {t(
                benefits?.hasFreeShipping
                  ? taxes.freeDeliveryTranslationCode
                  : taxes.generalTranslationCode,
              )}{' '}
              {!isDrawerNotNeeded && onTaxDetailsClick && (
                <button
                  className="hyperlink"
                  onClick={onTaxDetailsClick}
                  type="button"
                >
                  {t(
                    benefits?.hasFreeShipping
                      ? t('benefits.fees.freeDeliveryDrawerButton')
                      : t('benefits.fees.drawerButton'),
                  )}
                </button>
              )}
            </p>
          </div>
        )}
      </AccordionItem>
    </Accordion>
  );
};
